<template>
  <div class="filter-tabs part-options-wrapper">
    <div class="md-tabs md-theme-default">
      <div class="md-tabs-navigation">
        <button
          v-for="(group, idx) in groupedOptions"
          :key="`${idx}-${group.type}`"
          type="button"
          class="md-button"
          :class="{ 'md-active': String(idx) === activeTabId }"
          @click="onTabHeaderClickByIndex(idx)">
          <span class="md-button-content">
            <div class="thumb-tab">
              <div
                class="filterIcon"
                :class="{ 'filterIconGrayedOut': hasSelectedItems(String(group.type)) }"
                :style="{
                  color: (!hasSelectedItems(String(group.type)) && String(idx) === activeTabId && getIconSpec(String(group.type))[1] === 'caret-large-down') ? '#d72129' : ''
                }"
              >
                <font-awesome-icon :icon="getIconSpec(String(group.type))" />
              </div>
              <div class="thumb-text" :class="{ 'thumb-textGrayedOut': hasSelectedItems(String(group.type)) }">
                <h3
                  class="thumb-tab-heading"
                  :class="{ 'thumb-tab-heading_hasItems': hasSelectedItems(String(group.type)) }"
                  :style="{
                    color: (!hasSelectedItems(String(group.type)) && String(idx) === activeTabId && getIconSpec(String(group.type))[1] === 'caret-large-down') ? '#d72129' : ''
                  }"
                >
                  {{ group.label }}
                </h3>
                <div
                  class="thumb-tab-subtitle"
                  :class="{ 'thumb-tab-subtitle_default': !selectedDescriptionsByLabel[group.label] }">
                  {{ selectedDescriptionsByLabel[group.label] || 'Select an Option' }}
                </div>
              </div>
            </div>
          </span>
        </button>
        <div class="md-tabs-indicator"></div>
      </div>

      <div class="md-tabs-content">
        <div class="filter-tabContentActive">
          <div
            v-for="option in (activeGroup && activeGroup.items ? activeGroup.items : [])"
            :key="option.OptionID"
            class="filter-tabContentItem"
            @click="onOptionClick(String(activeGroup.type), option)">
            <figure
              :class="{
                'incompatible': String(activeGroup.type) !== '2' && compatibleOptions && !compatibleOptions.has(option.OptionID),
                'group-locked': isGroupLocked(String(activeGroup.type)),
                'swap-capable': String(activeGroup.type) !== '2' && compatibleOptions && !compatibleOptions.has(option.OptionID) && !isGroupLocked(String(activeGroup.type)),
                'card-active': isSelected(String(activeGroup.type), option),
                'card': !isSelected(String(activeGroup.type), option)
              }"
              :tabindex="String(activeGroup.type) !== '2' && isGroupLocked(String(activeGroup.type)) && compatibleOptions && !compatibleOptions.has(option.OptionID) ? 0 : -1">
              <img
                :class="[
                  'filter-thumb-img',
                  { 'incompatible': String(activeGroup.type) !== '2' && compatibleOptions && !compatibleOptions.has(option.OptionID) }
                ]"
                :src="`/Portals/0/${option.OptionImage}`"
                :alt="option.OptionVisualDescription || option.OptionName">
              <span v-if="cadViewerEnabled && (option.Has3D === true || option.VaultAvailable === true)" class="icon-3d-badge">
                <i v-if="$root.$options.components.FontAwesomeIcon" class="fa fa-cube"></i>
                <span v-else>3D</span>
              </span>
              <div
                v-if="String(activeGroup.type) !== '2' && isGroupLocked(String(activeGroup.type)) && compatibleOptions && !compatibleOptions.has(option.OptionID)"
                class="incompatible-overlay-message">
                Not available with your current selections.
              </div>
              <md-tooltip
                v-if="String(activeGroup.type) !== '2' && !isGroupLocked(String(activeGroup.type)) && compatibleOptions && !compatibleOptions.has(option.OptionID)"
                md-direction="top">
                Click to swap your {{ getOptionTypeLabel(String(activeGroup.type)) }} choice.
              </md-tooltip>
              <figcaption>
                <span>{{ option.OptionVisualDescription || option.OptionName }}</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { CAD_VIEWER_ENABLED } from '../../utilities/featureFlags';

export default {
  name: 'part-options-new',
  props: {
    items: { type: Array, default: () => [] },
    combos: { type: Array, default: () => [] },
    basePartId: { type: String, default: '' },
    optionTypes: { type: Array, default: () => [] },
    selectedAttributes: { type: Array, default: () => [] }
  },
  data() {
    return {
      cadViewerEnabled: CAD_VIEWER_ENABLED,
      activeTab: null, // string id of current tab
      activeTabIndex: 0, // numeric index for md-tabs
      activeTabId: '0', // string id for tab change tracing
      selectedOptions: {}, // selections as { groupType: { optionId: true } }
      compatibleOptions: null, // Set of compatible option IDs
      comboCompatibilityMap: new Map(), // optionId -> Set<optionId> from combo matrix
      isHydratingFromParent: false,
      lastEmittedPartId: null,
      lastEmittedVisualPrice: null,
      cachedItems: [] // last known non-empty items — prevents blank pane during API transitions
    }
  },
  computed: {
    groupedOptions() {
      const norm = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
      const toKey = (v) => String(norm(v));

      // Use cached items as fallback to prevent blank pane during API transitions
      const effectiveItems = (Array.isArray(this.items) && this.items.length > 0)
        ? this.items
        : this.cachedItems;

      // If optionTypes are available, build groups in that exact order
      if (Array.isArray(this.optionTypes) && this.optionTypes.length > 0) {
        // Stable sort by numeric OptionOrder; fallback to original index
        const sortedTypes = (this.optionTypes || [])
          .map((ot, idx) => ({
            ot,
            idx,
            order: (() => {
              const n = Number(norm(ot.OptionOrder));
              return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
            })()
          }))
          .sort((a, b) => (a.order - b.order) || (a.idx - b.idx))
          .map(x => x.ot);

        return sortedTypes.map(ot => {
          const typeKey = toKey(ot.OptionType);
          // Include all items regardless of Has3D/VaultAvailable
          const items = (effectiveItems || []).filter(it => toKey(it.OptionType) === typeKey);
          const label = (ot.OptionName && typeof ot.OptionName === 'object' && 'value' in ot.OptionName)
            ? ot.OptionName.value
            : ot.OptionName || `Option Type ${typeKey}`;
          return { type: typeKey, items, label };
        });
      }

      // Fallback: group from items, natural order
      const groups = (effectiveItems || []).reduce((acc, item) => {
        const k = toKey(item.OptionType);
        if (!acc[k]) {
          acc[k] = { type: k, items: [] };
        }
        // Include all items regardless of Has3D/VaultAvailable
        acc[k].items.push(item);
        return acc;
      }, {});

      return Object.values(groups);
    },
    activeGroup() {
      const groups = this.groupedOptions || [];
      if (!groups.length) return null;

      const firstWithItems = groups.find(g => Array.isArray(g.items) && g.items.length > 0) || null;

      const byType = groups.find(g => String(g.type) === String(this.activeTab));
      if (byType && Array.isArray(byType.items) && byType.items.length > 0) {
        return byType;
      }

      const idx = Number(this.activeTabId);
      if (!Number.isNaN(idx) && groups[idx] && Array.isArray(groups[idx].items) && groups[idx].items.length > 0) {
        return groups[idx];
      }

      return firstWithItems || groups[0];
    },
  // Precompute selected descriptions per group for reliable reactivity in tab headers
    selectedDescriptionsByGroup() {
      const unwrap = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
      const result = {};
      (this.groupedOptions || []).forEach(group => {
        const typeKey = String(group.type);
        const selMap = (this.selectedOptions && this.selectedOptions[typeKey]) || {};
        const descs = (group.items || [])
          .filter(it => !!selMap[it.OptionID])
          .map(it => unwrap(it.OptionVisualDescription) || unwrap(it.OptionName))
          .filter(Boolean);
        if (descs.length) {
          result[typeKey] = descs.join(', ');
        }
      });
      return result;
    },
    // Map from label -> type for use in the header slot where only tab.label is available
    labelToTypeMap() {
      const map = {};
      (this.groupedOptions || []).forEach(g => { map[g.label] = String(g.type); });
      return map;
    },
    // Map from label -> joined descriptions for straightforward header binding
    selectedDescriptionsByLabel() {
      const byLabel = {};
      (this.groupedOptions || []).forEach(g => {
        const key = g.label;
        const typeKey = String(g.type);
        const selMap = (this.selectedOptions && this.selectedOptions[typeKey]) || {};
        const unwrap = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
        const descs = (g.items || [])
          .filter(it => !!selMap[it.OptionID])
          .map(it => unwrap(it.OptionVisualDescription) || unwrap(it.OptionName))
          .filter(Boolean);
        if (descs.length) byLabel[key] = descs.join(', ');
      });
      return byLabel;
    }
  },
  methods: {
    getSelectionDebugState() {
      const selectedByGroup = Object.entries(this.selectedOptions || {}).reduce((acc, [groupType, optionMap]) => {
        const selectedIds = Object.entries(optionMap || {})
          .filter(([, isSelected]) => !!isSelected)
          .map(([optionId]) => String(optionId))

        if (selectedIds.length) {
          acc[String(groupType)] = selectedIds
        }

        return acc
      }, {})

      return {
        activeTab: this.activeTab,
        activeTabId: this.activeTabId,
        selectedByGroup,
        selectedIds: this.getSelectedIds().map(id => String(id))
      }
    },


    // Centralized debug logger for tab/animation diagnostics
    dbg(label, payload = {}) {
      try {
        const nav = this.$el && this.$el.querySelector('.md-tabs-navigation');
        const buttons = nav ? Array.from(nav.querySelectorAll('.md-button')) : [];
        const activeBtnIndex = buttons.findIndex(b => b.classList.contains('md-active'));
        const indicator = this.$el && this.$el.querySelector('.md-tabs-indicator');
        const indicatorStyle = indicator ? {
          left: indicator.style.left,
          width: indicator.style.width,
          transform: indicator.style.transform
        } : null;
        const refInfo = this.$refs && this.$refs.optionsTabs ? {
          hasRef: true,
          hasSetActiveTab: typeof this.$refs.optionsTabs.setActiveTab === 'function'
        } : { hasRef: false };
        console.log(`[PartOptionsNew] ${label}`, {
          activeTabId: this.activeTabId,
          activeTabIndex: this.activeTabIndex,
          activeTab: this.activeTab,
          headerActiveIndex: activeBtnIndex,
          indicatorStyle,
          refInfo,
          ...payload
        });
      } catch (e) {
        console.log(`[PartOptionsNew] ${label} (log error)`, e);
      }
    },
    typeForLabel(label) {
      const map = this.labelToTypeMap || {};
      return map[label] ? String(map[label]) : String(label);
    },
    // Returns true when none of the options in the given group are compatible
    // Exception: Type 2 options are never locked
    isGroupLocked(groupType) {
      const typeStr = String(groupType);
      // Type 2 options are never locked
      if (typeStr === '2') return false;
      if (!this.compatibleOptions || !(this.items && this.items.length)) return false;
      const anyCompatibleInGroup = this.items.some(it => String(it.OptionType) === typeStr && this.compatibleOptions.has(it.OptionID));
      return !anyCompatibleInGroup;
  },

    getGroupTypeByTabId(tabId) {
      const idStr = String(tabId);
      const idx = Number(idStr);
      const grp = Array.isArray(this.groupedOptions) ? this.groupedOptions[idx] : null;
      return grp ? String(grp.type) : this.activeTab || idStr;
    },
    getGroupLabel(id) {
      const idStr = String(id);
      const group = (this.groupedOptions || []).find(g => String(g.type) === idStr);
      if (group && group.label) return group.label;
      // Fallback to computed label from optionTypes
      return this.getOptionTypeLabel(idStr);
    },
    syncActiveTabState() {
      const groups = this.groupedOptions || [];
      if (!groups.length) {
        this.activeTab = null;
        this.activeTabId = '0';
        this.activeTabIndex = 0;
        return;
      }

      let idx = groups.findIndex(g => String(g.type) === String(this.activeTab));
      const hasItemsAtIdx = idx >= 0 && Array.isArray(groups[idx]?.items) && groups[idx].items.length > 0;
      if (!hasItemsAtIdx) {
        const firstWithItems = groups.findIndex(g => Array.isArray(g.items) && g.items.length > 0);
        idx = firstWithItems >= 0 ? firstWithItems : 0;
      }

      this.activeTabIndex = idx;
      this.activeTabId = String(idx);
      this.activeTab = String(groups[idx].type);
    },
    onTabHeaderClickByIndex(idx) {
      const groups = this.groupedOptions || [];
      if (!groups[idx]) return;
      const idStr = String(idx);
      const nextType = this.getGroupTypeByTabId(idStr);
      // ...removed TRACE log...

      if (idStr === this.activeTabId) {
        return;
      }

      this.activeTabIndex = idx;
      this.activeTabId = idStr;
      this.activeTab = nextType;
      this.$nextTick(() => {
        this.refreshTabsLayout();
      });
    },

    refreshTabsLayout() {
      if (!this.$el) return;
      const content = this.$el.querySelector('.md-tabs-content');
      if (!content) return;
      // Keep height automatic so state transitions cannot leave the options pane collapsed.
      content.style.height = 'auto';
    },
    
    toggleSelection(groupType, option) {
      // ...removed TRACE log...

      if (!this.selectedOptions[groupType]) {
        this.$set(this.selectedOptions, groupType, {})
      }
      
      const newState = !this.selectedOptions[groupType][option.OptionID];
      
      // Toggle the selection using Vue.set to ensure reactivity
      this.$set(
        this.selectedOptions[groupType], 
        option.OptionID, 
        newState
      )
      
      // Recompute compatibility immediately so UI updates in same tick
      this.updateCompatibleOptions()
      this.emitResolvedPartId()
      
      // Emit the selection change
      this.$emit('selection-changed', {
        groupType,
        optionId: option.OptionID,
        selected: newState
      })

      // ...removed TRACE log...
    },

    // Exclusive select: clear current selections in the group and select only this option
    selectExclusive(groupType, option) {
      // ...removed TRACE log...

      this.$set(this.selectedOptions, groupType, {});
      this.$set(this.selectedOptions[groupType], option.OptionID, true);

      // Recompute compatibility and emit part id immediately
      this.updateCompatibleOptions();
      this.emitResolvedPartId();

      this.$emit('selection-changed', {
        groupType,
        optionId: option.OptionID,
        selected: true,
        exclusive: true
      });

      // ...removed TRACE log...
    },

    // Handle clicks taking into account incompatibility and group lock state
    onOptionClick(groupType, option) {
      // ...removed TRACE log...

      const isIncompatible = this.compatibleOptions && !this.compatibleOptions.has(option.OptionID);
      const isType2 = this.isType2Option(option);
      
      // Special handling for type 2 options
      if (isType2) {
        // If selecting a type 2 option (not deselecting), deselect incompatible options first
        if (!this.isSelected(groupType, option)) {
          this.deselectIncompatibleOptions(option);
        }
        // Then toggle the type 2 option selection
        this.toggleSelection(groupType, option);
        return;
      }
      
      // Standard handling for non-type-3 options
      // Ignore entirely if no compatible options exist in this group
      if (this.isGroupLocked(groupType)) return;
      // Swap to incompatible within same group (exclusive select)
      if (isIncompatible) {
        this.selectExclusive(groupType, option);
        return;
      }
      // Toggle compatible selection
      this.toggleSelection(groupType, option);
    },
    
    isSelected(groupType, option) {
      return this.selectedOptions[groupType]?.[option.OptionID] || false
    },

    // Check if an option is type 2
    isType2Option(option) {
      const norm = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
      return String(norm(option.OptionType)) === '2';
    },

    // Deselect options that are incompatible with the given type 2 option
    deselectIncompatibleOptions(type2Option) {
      if (!this.isType2Option(type2Option)) return;

      const selectedIds = this.getSelectedIds();
      const incompatibleIds = [];

      // Find which selected options are NOT compatible with this type 2 option
      for (const selectedId of selectedIds) {
        // Skip the type 2 option itself
        if (selectedId === type2Option.OptionID) continue;

        // Check if there exists a combo that contains BOTH the type 2 option AND this selected option
        const isCompatibleWithType2 = this.combos.some(combo => {
          const comboOptionIds = combo.OptionCombo.split(',').map(id => id.trim());
          return comboOptionIds.includes(type2Option.OptionID) && comboOptionIds.includes(selectedId);
        });

        if (!isCompatibleWithType2) {
          incompatibleIds.push(selectedId);
        }
      }

      // ...removed TRACE log...

      // Deselect all incompatible options from all groups
      for (const incompatibleId of incompatibleIds) {
        // Find which group this option belongs to
        for (const [groupType, selectedMap] of Object.entries(this.selectedOptions)) {
          if (selectedMap && selectedMap[incompatibleId]) {
            this.$set(this.selectedOptions[groupType], incompatibleId, false);
          }
        }
      }
    },
    
    hasSelectedItems(groupType) {
      return this.selectedOptions[groupType] && 
             Object.values(this.selectedOptions[groupType]).some(selected => selected)
    },
    
    getIconSpec(groupType) {
      // Selected should always show a check immediately, even on the active tab.
      if (this.hasSelectedItems(groupType)) {
        return ['far', 'circle-check'];
      }
      // Active tab (with no selection): solid caret-down
      if (groupType === this.activeTab) {
        return ['fass', 'caret-large-down'];
      }
      // Default: solid caret-right (large)
      return ['fass', 'caret-large-right'];
    },

    // Return array of selected OptionVisualDescription (fallback to OptionName) for a group
    getSelectedDescriptions(groupType) {
      const typeStr = String(groupType);
      const selectedMap = this.selectedOptions?.[typeStr] || {};
      const unwrap = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;

      // Build a lookup by OptionID for fast access
      const byId = new Map((this.items || []).map(it => [String(it.OptionID), it]));

      return Object.entries(selectedMap)
        .filter(([, isSelected]) => !!isSelected)
        .map(([id]) => byId.get(String(id)))
        .filter(Boolean)
        .map(it => unwrap(it.OptionVisualDescription) || unwrap(it.OptionName))
        .filter(Boolean);
    },
    
    getOptionTypeLabel(type) {
      const optionType = this.optionTypes.find(ot => {
        // Handles both plain and observable OptionType
        const optionTypeValue = ot.OptionType?.value ?? ot.OptionType;
        return optionTypeValue == type;
      });
      if (optionType) {
        // Handles both plain and observable OptionName
        return optionType.OptionName?.value ?? optionType.OptionName ?? `Option Type ${type}`;
      }
      return `Option Type ${type}`;
  },

  sortGroupedOptions(groupArray) {
      // Normalize helper to unwrap observables and coerce to string keys
      const norm = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
      const key = (v) => String(norm(v));

      // Build maps for OptionOrder and fallback to the order the API returned optionTypes
      const orderMap = new Map();
      const indexMap = new Map();
      (this.optionTypes || []).forEach((ot, idx) => {
        const k = key(ot.OptionType);
        const order = Number(norm(ot.OptionOrder));
        if (!orderMap.has(k) && !Number.isNaN(order)) {
          orderMap.set(k, order);
        }
        if (!indexMap.has(k)) {
          indexMap.set(k, idx);
        }
      });

      // Sort by OptionOrder when available; otherwise by position in optionTypes; then by type key
      return groupArray.slice().sort((a, b) => {
        const aKey = key(a.type);
        const bKey = key(b.type);
        const aOrder = orderMap.has(aKey) ? orderMap.get(aKey) : Number.MAX_VALUE;
        const bOrder = orderMap.has(bKey) ? orderMap.get(bKey) : Number.MAX_VALUE;
        if (aOrder !== bOrder) return aOrder - bOrder;

        const aIdx = indexMap.has(aKey) ? indexMap.get(aKey) : Number.MAX_VALUE;
        const bIdx = indexMap.has(bKey) ? indexMap.get(bKey) : Number.MAX_VALUE;
        if (aIdx !== bIdx) return aIdx - bIdx;

        return aKey.localeCompare(bKey);
      });
    },

    // Get the currently selected item from Items array
    getSelectedItems() {
      const selectedItems = [];
      this.items.forEach(item => {
        Object.entries(this.selectedOptions).forEach(([, options]) => {
          if (options[item.OptionID]) {
            selectedItems.push(item);
          }
        });
      });
      return selectedItems;
    },

    // Get the currently selected OptionIDs (flat array of ids)
    getSelectedIds() {
      const selectedItems = this.getSelectedItems();
      return selectedItems.map(item => item.OptionID);
    },

    // Check if an option is compatible with current selections by checking against OptionCombo
    isOptionCompatible(option) {
      const selectedIds = this.getSelectedIds();
      // nothing selected -> option is compatible
      if (selectedIds.length === 0) return true;

      const optionId = String(option.OptionID);
      const candidateSet = this.comboCompatibilityMap.get(optionId);
      if (!candidateSet) return false;

      // Candidate must appear in at least one combo together with all selected IDs.
      return selectedIds.every(id => candidateSet.has(String(id)));
    },

    // For debugging
    logCompatibilityCheck(option) {
      const selectedItems = this.getSelectedItems();
      console.log('Checking compatibility for:', {
        option: option.OptionID,
        optionCsv: option.OptionIDsCsv,
        selectedItems: selectedItems.map(item => ({
          id: item.OptionID,
          csv: item.OptionIDsCsv
        })),
        availableCombos: this.combos.map(combo => combo.OptionIDsCsv)
      });
    },

    rebuildComboCompatibilityMap() {
      const map = new Map();
      const combos = Array.isArray(this.combos) ? this.combos : [];

      for (const combo of combos) {
        const ids = String(combo?.OptionCombo || '')
          .split(',')
          .map(id => id.trim())
          .filter(Boolean);

        if (!ids.length) continue;

        for (const id of ids) {
          if (!map.has(id)) {
            map.set(id, new Set());
          }
          const set = map.get(id);
          for (const relatedId of ids) {
            set.add(relatedId);
          }
        }
      }

      this.comboCompatibilityMap = map;
    },

    // Update compatibility for all options
    updateCompatibleOptions() {
      const allItems = (Array.isArray(this.items) && this.items.length > 0)
        ? this.items
        : (this.cachedItems || []);
      const selectedIds = this.getSelectedIds().map(id => String(id));

      if (!allItems.length) {
        this.compatibleOptions = new Set();
        return;
      }

      if (!selectedIds.length) {
        this.compatibleOptions = new Set(allItems.map(item => String(item.OptionID)));
        return;
      }

      let intersection = null;
      for (const selectedId of selectedIds) {
        const setForSelected = this.comboCompatibilityMap.get(String(selectedId));
        if (!setForSelected) {
          intersection = new Set();
          break;
        }
        if (intersection === null) {
          intersection = new Set(setForSelected);
        } else {
          intersection = new Set([...intersection].filter(id => setForSelected.has(id)));
        }
      }

      const selectedSet = new Set(selectedIds);
      const compatible = intersection || new Set();
      for (const id of selectedSet) {
        compatible.add(id);
      }
      this.compatibleOptions = compatible;
    },

    // Find a combo whose OptionCombo exactly matches the set of selected option IDs
    getExactMatchCombo() {
      const combos = this.combos || [];
      const selectedIds = this.getSelectedIds().map(id => String(id));
      if (!combos.length || selectedIds.length === 0) return null;

      const selectedSet = new Set(selectedIds);
      for (const combo of combos) {
        const comboIds = (combo.OptionCombo || '')
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
          .map(id => String(id));
        if (comboIds.length !== selectedIds.length) continue;
        // Check set equality
        const comboSet = new Set(comboIds);
        let equal = true;
        for (const id of selectedSet) {
          if (!comboSet.has(id)) { equal = false; break; }
        }
        if (equal) {
          return combo;
        }
      }
      return null;
    },

    // Emit the resolved PartID (BasePartAndOptionsDashSansRS) and VisualPrice for the exact match combo, or null
    emitResolvedPartId() {
      const match = this.getExactMatchCombo();
      let partId = match && (match.BasePartAndOptionsDashSansRS || match.basePartAndOptionsDashSansRS) || null;
      const visualPrice = match && (match.VisualPrice || match.visualPrice) || null;
      // If no match, fallback to base part ID
      if (!partId) {
        // Prefer explicit base part ID from parent.
        partId = this.basePartId || null;
        // Try to get base part from combos or props
        if (!partId && Array.isArray(this.combos) && this.combos.length > 0) {
          partId = this.combos[0].BasePartID || this.combos[0].basePartID || this.items?.[0]?.BasePartID || this.items?.[0]?.basePartID || null;
        }
        if (!partId && this.$props && this.$props.items && this.$props.items.length > 0) {
          partId = this.$props.items[0].BasePartID || this.$props.items[0].basePartID || null;
        }
        // As a last resort, try to use the parent productDetails.PartID
        if (!partId && this.$parent && this.$parent.productDetails) {
          partId = this.$parent.productDetails.PartID || null;
        }
      }

      if (partId === this.lastEmittedPartId && visualPrice === this.lastEmittedVisualPrice) {
        return;
      }

      this.lastEmittedPartId = partId;
      this.lastEmittedVisualPrice = visualPrice;

      // ...removed TRACE log...
      this.$emit('part-id-changed', { partId, visualPrice });
    }
  },
  mounted() {
      // Seed the cache immediately so groupedOptions is never blank on first render
      if (Array.isArray(this.items) && this.items.length > 0) {
        this.cachedItems = [...this.items];
      }
      this.syncActiveTabState();
      this.rebuildComboCompatibilityMap();
      this.updateCompatibleOptions();
      this.$nextTick(() => {
        this.refreshTabsLayout();
      });
  },
  watch: {
    selectedAttributes: {
      immediate: true,
      handler(newVal) {
        if (!Array.isArray(newVal)) {
          return;
        }

        this.isHydratingFromParent = true;

        if (!newVal.length) {
          this.selectedOptions = {};
          this.syncActiveTabState();
          this.updateCompatibleOptions();
          this.$nextTick(() => {
            this.isHydratingFromParent = false;
          });
          return;
        }

        // Only process array of strings (not array of objects)
        if (typeof newVal[0] !== 'string') {
          this.$nextTick(() => {
            this.isHydratingFromParent = false;
          });
          return;
        }

        const dashString = newVal.join('-');
        const dashStringUpper = dashString.toUpperCase();
        const basePartIdUpper = String(this.basePartId || '').toUpperCase();
        const availableItems = (Array.isArray(this.items) && this.items.length > 0)
          ? this.items
          : (this.cachedItems || []);

        let matchedCombo = null;
        if (Array.isArray(this.combos)) {
          matchedCombo = this.combos.find(combo => {
            // Backend SelectedOptions are option suffixes like ["4SL"], while combo rows
            // carry full part IDs like "S-WH-5NYB-4SL".
            const comboDash = (combo.BasePartAndOptionsDashSansRS || combo.basePartAndOptionsDashSansRS || '')
              .toString()
              .toUpperCase();
            if (!comboDash) return false;

            if (comboDash === dashStringUpper) {
              return true;
            }

            if (!basePartIdUpper) {
              return false;
            }

            const prefix = `${basePartIdUpper}-`;
            if (!comboDash.startsWith(prefix)) {
              return false;
            }

            return comboDash.slice(prefix.length) === dashStringUpper;
          });
        }
        this.selectedOptions = {};
        if (matchedCombo) {
          const comboOptionIds = (matchedCombo.OptionCombo || '').split(',').map(id => id.trim());
          comboOptionIds.forEach(optionId => {
            const item = availableItems.find(it => String(it.OptionID) === optionId);
            if (item) {
              const groupType = String(item.OptionType);
              if (!this.selectedOptions[groupType]) {
                this.$set(this.selectedOptions, groupType, {});
              }
              this.$set(this.selectedOptions[groupType], item.OptionID, true);
            }
          });
        } else {
          // Fallback: match longest possible dash-joined segments in items
          let remaining = dashString;
          let safety = 0;
          while (remaining.length > 0 && safety < 100) {
            let found = false;
            for (let len = Math.min(remaining.length, 50); len > 0; len--) {
              const sub = remaining.substring(0, len);
              const match = availableItems.find(item => {
                const name = (item.OptionName || '').toString().replace(/\s+/g, '').toUpperCase();
                const vis = (item.OptionVisualDescription || '').toString().replace(/\s+/g, '').toUpperCase();
                return name === sub.replace(/\s+/g, '').toUpperCase() || vis === sub.replace(/\s+/g, '').toUpperCase();
              });
              if (match) {
                const groupType = String(match.OptionType);
                if (!this.selectedOptions[groupType]) {
                  this.$set(this.selectedOptions, groupType, {});
                }
                this.$set(this.selectedOptions[groupType], match.OptionID, true);
                // Remove matched segment and dash
                remaining = remaining.substring(len);
                if (remaining.startsWith('-')) remaining = remaining.substring(1);
                found = true;
                break;
              }
            }
            if (!found) break;
            safety++;
          }
          if (safety >= 100) {
            console.warn('[PartOptionsNew] selectedAttributes watcher safety break', dashString, newVal);
          }
        }
        this.updateCompatibleOptions();
        this.syncActiveTabState();
        this.$nextTick(() => {
          this.isHydratingFromParent = false;
        });
        // ...removed TRACE log...
      }
    },
    combos() {
      // ...removed TRACE log...
      this.rebuildComboCompatibilityMap();
      this.updateCompatibleOptions();
      // If selectedAttributes was already set before combos arrived, re-run hydration
      // so that the combo match can succeed now that the combo data is available.
      if (
        !this.isHydratingFromParent &&
        Array.isArray(this.selectedAttributes) &&
        this.selectedAttributes.length > 0
      ) {
        this.$options.watch.selectedAttributes.handler.call(this, this.selectedAttributes);
        return;
      }
      if (!this.isHydratingFromParent) {
        this.emitResolvedPartId();
      }
    },
    items() {
      // ...removed TRACE log...
      // Keep cache up-to-date so groupedOptions never goes blank during API transitions
      if (Array.isArray(this.items) && this.items.length > 0) {
        this.cachedItems = [...this.items];
      }
      this.updateCompatibleOptions();
      // If selectedAttributes was already set before items arrived, re-run hydration.
      if (
        !this.isHydratingFromParent &&
        Array.isArray(this.selectedAttributes) &&
        this.selectedAttributes.length > 0
      ) {
        this.$options.watch.selectedAttributes.handler.call(this, this.selectedAttributes);
      } else if (!this.isHydratingFromParent) {
        this.emitResolvedPartId();
      }
      this.syncActiveTabState();
      this.$nextTick(() => {
        this.refreshTabsLayout();
      });
    },
    optionTypes() {
      this.syncActiveTabState();
      this.$nextTick(() => {
        this.refreshTabsLayout();
      });
    }
  }
}
</script>

<style lang="scss">
  @import "../../assets/variables";

  .icon-3d-badge {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    background: none;
    color: inherit;
    font-size: 0.85rem;
    border-radius: 0.3em;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 0.25em;
    pointer-events: none;
  }
  .filter-thumb-img {
    position: relative;
  }

  .part-options-wrapper {
    // Inherit FilterTabs styling
    &.filter-tabs {
      --tab-font-size: 1.25rem;
      @media screen and (min-width: $medium)  { --tab-font-size: 1rem; }
      @media screen and (min-width: $large)  { --tab-font-size: 1.25rem; }
      @media screen and (min-width: $x-large)  { --tab-font-size: 1.5rem; }
      @media screen and (min-width: $xxx-large)  { --tab-font-size: 2rem; }
      
      position: relative;
      width: 100%;
      margin: 1rem 0;
      
      .md-tabs {
        &.md-theme-default {
          .md-tabs-navigation {
            display: flex;
            background-color: transparent;
            border-bottom: 1px solid $borderColor;
            // Ensure tab headers can grow vertically to fit subtitle text
            height: auto;
            align-items: stretch;
            padding-bottom: 0px;
            margin-bottom: 10px;
            // Adjust inner ripple container padding within option headers
            .md-ripple {
              padding: 0 11px !important;
            }
            
            .md-button {
              display: flex;
              align-items: stretch;
              flex: 0 0 auto;
              font-size: var(--tab-font-size);
              // Do not force a color here so inner heading/subtitle control the color
              color: inherit;
              opacity: 0.83;
              text-transform: none;
              margin-right: 0;
              transition: none;
              text-align: left; // align all header text left
              padding-left: 12px;
              padding-right: 12px;
              padding-top: 0px;
              padding-bottom: 0px;
              line-height: 1.1;
              white-space: normal;
              cursor: pointer;
              border: 0;

              .md-button-content {
                display: flex;
                align-items: center;
                justify-content: flex-start; // left align contents inside tab button
                width: 100%;
              }
              &:hover,
              &:focus,
              &:active {
                // Keep appearance identical on hover/focus
                opacity: 1;
                background-color: transparent !important;
                box-shadow: none !important;
                color: inherit !important;
              }
              &.md-active {
                opacity: 1;
              }
            }

            // Strong override to neutralize theme hover background on buttons
            .md-button,
            .md-button:hover,
            .md-button:focus,
            .md-button:active {
              background-color: transparent !important;
            }

            // Neutralize the Vue Material button overlay that appears via ::before on hover/focus/active
            .md-button::before,
            .md-button:hover::before,
            .md-button:focus::before,
            .md-button:active::before {
              background-color: transparent !important;
            }
          }
            
          .md-tabs-indicator {
            background-color: #000;
          }
          
          .md-tabs-content {
            min-height: 133px !important;
            height: auto !important;
            overflow: visible;
          }
        }
      }
      
      .md-tab{
        padding: 16px 0px 0px 5px;
      }
      .thumb-tab {
                // When tab is active (caret down), make caret and label red
                // Force caret and label to red when tab is active (caret down, not selected)
                .md-active & {
                  .filterIcon svg[data-icon="caret-large-down"] {
                    color: #d72129 !important;
                    fill: #d72129 !important;
                    stroke: #d72129 !important;
                  }
                  .thumb-tab-heading:not(.thumb-tab-heading_hasItems) {
                    color: #d72129 !important;
                  }
                }
        display: flex;
        flex-direction: row;
        align-items: center;
        
        .filterIcon {
          width: 18px;
          margin-top: -10px;
          margin-right: 0.55rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 60px; // triangle/check size
          color: #bfbfbf; // default: light gray

          // ACTIVE (clicked, not selected)
          .md-active & {
            color: #d72129;
          }

          // SELECTED (option chosen)
          &.filterIconGrayedOut {
            color: rgba(109,110,112,1); // selected but not active
          }

          // Optical size adjustments
          svg[data-icon="circle-check"] {
            transform: scale(0.5);
            transform-origin: center;
          }
          svg[data-icon="caret-large-right"] {
            transform: scale(0.5);
            transform-origin: center;
          }
          svg[data-icon="caret-large-down"] {
            transform: scale(0.5);
            transform-origin: center;
          }
        }
        
        .thumb-text {
          .thumb-tab-heading,
          .thumb-tab-heading_hasItems {
            font-size: 20px;
            font-weight: 700;
            font-family: 'Roboto', Arial, Helvetica, sans-serif;
            margin: 0;
            text-decoration: none;
            transition: none;
            line-height: 22px;
          }
          // Default not-active, no selection
          .thumb-tab-heading { color: #bfbfbf; }
          // ACTIVE (clicked, not selected)
          .md-active & .thumb-tab-heading:not(.thumb-tab-heading_hasItems) {
            color: #d72129;
          }
          // Selected (not active)
          .thumb-tab-heading_hasItems { color:rgba(109,110,112,1); }
          
          // Container gray affects subtitle and icon when selected and not active
          &.thumb-textGrayedOut {
            color: #999;
          }

          .thumb-tab-subtitle {
            font-size: 12px;
            line-height: 1.2;
            margin-top: 0.125rem;
            color: #555;
            // Reserve enough space for up to two lines to avoid layout shift
            min-height: 28px;
            height: 28px;
            // Allow wrapping and clamp to 2 lines with ellipsis
            display: -webkit-box;
            line-clamp: 2; // standard property for compatibility
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;

            &.thumb-tab-subtitle_default {
              color: #d72027;
            }
          }
        }
      }
      .filter-tabContent {
        display: none;
      }
      
      .filter-tabContentActive {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
      }
      
      .filter-tabContentItem {
        // Lock each option item to a fixed 110px square footprint (width); height grows with caption below image
        flex: 0 0 180px;
        width: 180px;
        figure {
          cursor: pointer;
          display: block;
          position: relative;
          width: 173px; // fixed card width
          min-width: 173px;
          aspect-ratio: 1 / 1; // keep the card perfectly square
          box-sizing: border-box;
          border: 1px solid #bfbfbf; // Default inactive border
          border-radius: 5px;
          overflow: hidden;
          transition: border-color 0.2s ease, border-width 0.2s ease;
          
          figcaption {
            position: absolute;
            left: 6px;
            right: 6px;
            bottom: 6px;
            text-align: center;
            padding: 2px 3px;
            white-space: normal;
            width: auto; // span inside padding
            font-size: 12px;
            line-height: 1.2;
            background: rgba(255,255,255,0.85);
            border-radius: 3px;
            pointer-events: none; // do not block clicks
          }
          
          .filter-thumb-img {
            width: 100%;
            height: 90%;
            object-fit: contain;
            object-position: top center;
            vertical-align: top;
            border-radius: 5px;
            margin: 0 0 4px 0;
          }

          // Sentinel "No Selection" icon styling
          .no-selection-icon {
            width: 100%;
            height: 75%;
            margin-top: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #888;
            font-size: 65px;
          }
          &.card-active .no-selection-icon {
            color: #d72027;
          }

          &.card:hover {
            border: 1px solid #d72129;
          }

          &.card-active {
            border: 3px solid #d72129;
          }

          /* Default incompatible appearance: greyed out and disabled */
          &.incompatible {
            opacity: 0.5;
            pointer-events: none;
            img { filter: grayscale(100%); }
            figcaption { color: #999; }
          }
          /* Entire group locked: keep greyed, but allow hover for tooltip */
          &.incompatible.group-locked {
            cursor: not-allowed;
            pointer-events: auto;

            .incompatible-overlay-message {
              position: absolute;
              left: 8px;
              right: 8px;
              top: 8px;
              bottom: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
              padding: 0.5rem;
              font-size: 0.8rem;
              line-height: 1.2;
              font-weight: 400;
              color: $white;
              background: rgba(0, 0, 0, 0.6);
              border-radius: 4px;
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s ease;
            }

            &:hover,
            &:focus,
            &:focus-visible {
              opacity: 0.85;
              border-color: #666;

              .incompatible-overlay-message {
                opacity: 1;
              }
            }
          }
          /* Swap-capable within same group: no greyscale, clickable */
          &.swap-capable {
            opacity: 1;
            pointer-events: auto;
            cursor: pointer;
            img { filter: none; }
          }
        }
      }

      @media screen and (max-width: $medium) {
        .md-tab {
          padding: 8px 0 0 0;
        }

        .thumb-tab {
          align-items: flex-start;

          .filterIcon {
            width: 14px;
            margin-top: 0;
            margin-right: 0.35rem;
            font-size: 42px;
          }

          .thumb-text {
            .thumb-tab-heading,
            .thumb-tab-heading_hasItems {
              font-size: 16px;
              line-height: 17px;
            }

            .thumb-tab-subtitle {
              display: none;
            }
          }
        }

        .filter-tabContentActive {
          gap: 0.75rem;
        }

        .filter-tabContentItem {
          flex: 1 1 calc(50% - 0.375rem);
          width: calc(50% - 0.375rem);

          figure {
            width: 100%;
            min-width: 0;

            figcaption {
              font-size: 11px;
            }
          }
        }
      }

      @media screen and (max-width: $small) {
        .thumb-tab {
          .thumb-text {
            .thumb-tab-heading,
            .thumb-tab-heading_hasItems {
              font-size: 14px;
              line-height: 15px;
            }
          }
        }

        .filter-tabContentItem {
          flex: 1 1 100%;
          width: 100%;
        }
      }
    }
  }
</style>
